import { Avatar, Box, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { CalendarProvider } from "../models/user";
import { CalendarConnectionStatus } from "../util/calendarEvent";

const OutlookIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 6.5V17.5C22 18.33 21.33 19 20.5 19H10V5H20.5C21.33 5 22 5.67 22 6.5Z" fill="#1976D2" />
    <path d="M22 6.5V8L15.5 12.5L10 8V5H20.5C21.33 5 22 5.67 22 6.5Z" fill="#1E88E5" />
    <path d="M2 7.5V18.5C2 19.33 2.67 20 3.5 20H14V6H3.5C2.67 6 2 6.67 2 7.5Z" fill="#1565C0" />
    <ellipse cx="8" cy="13" rx="3.5" ry="3.5" fill="#0D47A1" />
    <path
      d="M8 10.5C6.62 10.5 5.5 11.62 5.5 13C5.5 14.38 6.62 15.5 8 15.5C9.38 15.5 10.5 14.38 10.5 13C10.5 11.62 9.38 10.5 8 10.5Z"
      fill="white"
      fillOpacity="0.9"
    />
  </svg>
);

const GmailIcon = () => (
  <svg width="22" height="16.5" viewBox="52 42 88 66" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285f4" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6" />
    <path fill="#34a853" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15" />
    <path fill="#fbbc04" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2" />
    <path fill="#ea4335" d="M72 74V48l24 18 24-18v26L96 92" />
    <path fill="#c5221f" d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2" />
  </svg>
);

function getInitials(email: string): string {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

interface CalendarSourceMarkerProps {
  provider?: string;
  accountUsername?: string;
  /** Optional connection health for this account. When omitted, no badge
   * is rendered (back-compat for callers that haven't wired the status map). */
  status?: CalendarConnectionStatus;
}

const CalendarSourceMarker = ({ provider, accountUsername, status }: CalendarSourceMarkerProps) => {
  const { t } = useTranslation();
  if (!provider && !accountUsername) return <Box sx={{ width: 52 }} />;

  const initials = accountUsername ? getInitials(accountUsername) : "?";
  const isGoogle = provider === CalendarProvider.Google;

  // Status badge: small icon overlaid on the bottom-right of the avatar.
  const statusMeta = (() => {
    if (!status) return null;
    switch (status) {
      case "active":
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 12, color: "#2E7D32" }} />,
          bg: "#FFFFFF",
          label: t("calendar.status.active"),
        };
      case "unavailable":
        return {
          icon: <CancelIcon sx={{ fontSize: 12, color: "#C62828" }} />,
          bg: "#FFFFFF",
          label: t("calendar.status.unavailable"),
        };
      case "revoked":
        return {
          icon: <CancelIcon sx={{ fontSize: 12, color: "#C62828" }} />,
          bg: "#FFFFFF",
          label: t("calendar.status.revoked"),
        };
      case "failed":
        return {
          icon: <WarningAmberIcon sx={{ fontSize: 12, color: "#ED6C02" }} />,
          bg: "#FFFFFF",
          label: t("calendar.status.failed"),
        };
      default:
        return null;
    }
  })();

  const tooltipLabel = statusMeta
    ? `${accountUsername || ""}${accountUsername ? " — " : ""}${statusMeta.label}`
    : accountUsername || "";

  return (
    <Tooltip title={tooltipLabel} arrow placement="top">
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, cursor: "default" }}>
        {isGoogle ? <GmailIcon /> : <OutlookIcon />}
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Avatar
            sx={[{
              width: 22,
              height: 22,
              fontSize: "0.6rem",
              fontWeight: 600,
              color: "#fff"
            }, isGoogle ? {
              backgroundColor: "#4285F4"
            } : {
              backgroundColor: "#1565C0"
            }]}
          >
            {initials}
          </Avatar>
          {statusMeta && (
            <Box
              aria-label={statusMeta.label}
              sx={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: statusMeta.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
              }}
            >
              {statusMeta.icon}
            </Box>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default CalendarSourceMarker;
